/**
 * DeleteLayoutModal Component
 *
 * Confirmation modal for deleting a saved layout.
 * Displays the layout name in the confirmation message.
 *
 * Follows UnsavedChangesDialog pattern: simple 2-button confirmation.
 * Adds isSubmitting state for async delete operation.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useToastNotification,
} from '@enfomentis/ui';

// ===== TYPES =====

export interface DeleteLayoutModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback on delete confirm */
  onConfirm: () => Promise<void>;
  /** Name of the layout being deleted (displayed in confirmation) */
  layoutName: string;
}

// ===== COMPONENT =====

export const DeleteLayoutModal: React.FC<DeleteLayoutModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  layoutName,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showToast } = useToastNotification();

  // Reset submitting state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleDelete = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete layout';
      showToast({ color: 'danger', title: 'Error', description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" placement="top-center">
      <ModalContent>
        <ModalHeader>Delete Layout</ModalHeader>
        <ModalBody>
          <p className="text-text-secondary">
            Delete layout &lsquo;{layoutName}&rsquo;? This action cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="danger" onPress={handleDelete} isLoading={isSubmitting}>
            Delete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
